'use client'

import {useEffect, useMemo, useState} from 'react'
import {AlertTriangle, Clock, Key, LogOut, Mail, MapPin, Save, Shield, UserCircle} from 'lucide-react'
import {apiGet, apiPut, getAuthUser, setAuthUser} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'
import {message} from '@/components/Toast'

type CurrentUserProfile = {
    id: number
    username: string
    email?: string | null
    role: string
    enabled: number
    isTrial?: number
    expiresAt?: number | null
    lastLoginAt?: number | null
    lastLoginIp?: string | null
    lastLoginLocation?: string | null
    lastSeenAt?: number | null
    createdAt: number
}

const EXPIRING_SOON_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

export function UserProfileView({onLogout}: {onLogout: () => void}) {
    const [username, setUsername] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [profile, setProfile] = useState<CurrentUserProfile | null>(null)
    const [profileLoading, setProfileLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const user = useMemo(() => getAuthUser(), [])
    const expiryNotice = useMemo(() => getExpiryNotice(profile?.expiresAt), [profile?.expiresAt])

    useEffect(() => {
        if (user) {
            setUsername(user.username)
        }
    }, [user])

    useEffect(() => {
        let mounted = true
        apiGet<{
            success: boolean
            user: CurrentUserProfile
        }>('/user/profile', {silent: true}).then((res) => {
            if (!mounted || !res.success || !res.user) return
            setUsername(res.user.username)
            setProfile(res.user)
        }).catch(() => {
            // Auth expiration is handled globally by api.ts.
        }).finally(() => {
            if (mounted) setProfileLoading(false)
        })
        return () => { mounted = false }
    }, [])

    async function handleSave() {
        if (newPassword && newPassword.length < 6) { message.error('密码至少6位'); return }
        if (newPassword && newPassword !== confirmPassword) { message.error('两次密码不一致'); return }
        setSaving(true)
        try {
            const body: Record<string, unknown> = {}
            if (newPassword) body.password = newPassword
            if (Object.keys(body).length === 0) { message.info('未修改任何信息'); setSaving(false); return }
            await apiPut(`/users/${user?.id}`, body)
            if (body.username) {
                const updated = {...user!}
                setAuthUser(updated)
                setProfile((current) => current ? {...current} : current)
            }
            setNewPassword('')
            setConfirmPassword('')
            message.success('保存成功')
        } catch { /* api.ts handles errors */ }
        finally { setSaving(false) }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="用户信息"/>

            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-3xl space-y-5">
                    <div className="bg-white rounded-lg border border-hair p-6 flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-green-50 text-accent flex items-center justify-center shrink-0">
                                <UserCircle size={36}/>
                            </div>
                            <div className="min-w-0">
                                <div className="text-2xl font-semibold text-ink truncate">{profileLoading ? '加载中...' : username || '-'}</div>
                                <div className="mt-1 text-sm text-muted">{profileLoading ? '-' : userRoleText(profile?.role, profile?.isTrial)}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onLogout}
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
                        >
                            <LogOut size={16}/> 退出登录
                        </button>
                    </div>

                    <div className="bg-white rounded-lg border border-hair overflow-hidden">
                        <div className="px-5 py-4 border-b border-hair flex items-center gap-2">
                            <Shield size={18} className="text-accent"/>
                            <h3 className="text-sm font-semibold text-ink">账户信息详情</h3>
                        </div>

                        <div className="p-5 space-y-5">
                            {expiryNotice.show && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                                    <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={18}/>
                                    <div>
                                        <div className="text-sm font-medium text-amber-800">账号即将到期</div>
                                        <div className="mt-1 text-sm text-amber-700">
                                            您的账号还有 {expiryNotice.days} 天到期，为了不影响使用，请及时续费。
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InfoField icon={Mail} label="邮箱地址" value={profileLoading ? '加载中...' : profile?.email || '-'}/>
                                <InfoField icon={Clock} label="账号有效期" value={profileLoading ? '加载中...' : formatExpiryTime(profile?.expiresAt)}/>
                                <InfoField icon={Clock} label="上次登录时间" value={profileLoading ? '加载中...' : formatUserTime(profile?.lastLoginAt)}/>
                                <InfoField icon={MapPin} label="登录 IP" value={profileLoading ? '加载中...' : profile?.lastLoginIp || '-'}/>
                                <InfoField icon={MapPin} label="上次登录地址" value={profileLoading ? '加载中...' : profile?.lastLoginLocation || '-'}/>
                            </div>

                            <div className="border-t border-hair pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <InfoField icon={UserCircle} label="用户名" value={profileLoading ? '加载中...' : profile?.username || '-'}/>
                                </div>
                                <div>
                                    <label className="block text-xs text-muted mb-1">新密码（留空则不修改）</label>
                                    <div className="relative">
                                        <input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="输入新密码"
                                            className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                                            autoComplete="new-password"
                                        />
                                        <Key size={15} className="absolute right-3 top-2.5 text-muted"/>
                                    </div>
                                </div>
                                {newPassword && (
                                    <div>
                                        <label className="block text-xs text-muted mb-1">确认新密码</label>
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="再次输入新密码"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="px-5 py-4 border-t border-hair bg-gray-50 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-5 py-2 text-sm text-white bg-accent hover:bg-accent-hover rounded-lg transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Save size={14}/> 保存修改
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

function InfoField({icon: Icon, label, value}: {icon: typeof Clock; label: string; value: string}) {
    return (
        <div>
            <div className="text-xs text-muted mb-1">{label}</div>
            <div className="min-h-10 rounded-lg border border-hair bg-gray-50 px-3 py-2 text-sm text-ink flex items-center gap-2">
                <Icon size={15} className="text-muted shrink-0"/>
                <span className="truncate">{value}</span>
            </div>
        </div>
    )
}

function getExpiryNotice(value?: number | null) {
    if (!value) return {show: false, days: 0}
    const diff = value - Date.now()
    if (diff <= 0 || diff > EXPIRING_SOON_DAYS * DAY_MS) return {show: false, days: 0}
    return {
        show: true,
        days: Math.max(1, Math.ceil(diff / DAY_MS)),
    }
}

function userRoleText(role?: string, isTrial?: number) {
    if (role === 'admin') return '管理员账号'
    if (isTrial === 1) return '试用用户账号'
    return '普通用户账号'
}

function formatUserTime(value?: number | null) {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

function formatExpiryTime(value?: number | null) {
    if (!value) return '永久有效'
    const text = formatUserTime(value)
    if (text === '-') return '-'
    return value <= Date.now() ? `${text}（已过期）` : text
}
